/**
    * @description      : 
    * @author           : USER
    * @group            : 
    * @created          : 04/09/2025 - 18:49:22
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 04/09/2025
    * - Author          : USER
    * - Modification    : Updated affiliates section with real images and responsive design
**/
// src/pages/about/About.js
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { 
  FaUsers, 
  FaGlobe, 
  FaAward, 
  FaHandshake,
  FaLightbulb,
  FaHeart,
  FaShieldAlt,
  FaPalette,
  FaHandsHelping,
  FaStar,
  FaUserFriends,
  FaGraduationCap
} from 'react-icons/fa';
import { oxford, learning, unihawk, absa, toloita } from '../../assets';

const About = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const coreValues = [
    {
      icon: FaAward,
      title: 'Excellence',
      description: 'Striving for the highest standards in everything we do.',
      color: 'text-green-600 bg-green-100'
    },
    {
      icon: FaUsers,
      title: 'Empowerment',
      description: 'Equipping children with confidence and leadership skills.',
      color: 'text-blue-600 bg-blue-100'
    },
    {
      icon: FaLightbulb,
      title: 'Innovation',
      description: 'Encouraging creativity and forward-thinking approaches.',
      color: 'text-yellow-600 bg-yellow-100'
    },
    {
      icon: FaShieldAlt,
      title: 'Integrity',
      description: 'Building trust and character through ethical practices.',
      color: 'text-purple-600 bg-purple-100'
    },
    {
      icon: FaPalette,
      title: 'Diversity',
      description: 'Embracing cultural richness and inclusivity.',
      color: 'text-pink-600 bg-pink-100'
    },
    {
      icon: FaHandsHelping,
      title: 'Collaboration',
      description: 'Working with families, schools, and partners to make an impact.',
      color: 'text-indigo-600 bg-indigo-100'
    }
  ];

  // Updated affiliates data - removed descriptions
  const affiliates = [
    {
      name: 'a learning lab',
      image: learning,
      hoverColor: 'hover:shadow-green-500/50'
    },
    {
      name: 'Unihawk',
      image: unihawk,
      hoverColor: 'hover:shadow-blue-500/50'
    },
    {
      name: 'Oxford Schools',
      image: oxford,
      hoverColor: 'hover:shadow-purple-500/50'
    },
    {
      name: 'Absa',
      image: absa,
      hoverColor: 'hover:shadow-red-500/50'
    },
    {
      name: 'Toloita Technologies',
      image: toloita,
      hoverColor: 'hover:shadow-indigo-500/50'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block px-4 py-2 bg-green-100 rounded-full border border-green-200 mb-6">
              <span className="text-green-700 text-sm font-medium">About African Child Debaters Champions LTD</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-6">
              <span className="text-gray-900">Empowering African Child Voices</span>
              <br />
              <span className="text-green-600">Building Global Champions</span>
            </h1>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
              African Child Debaters Champions LTD is a premier organization dedicated to nurturing confident, articulate, and well-rounded African children through world-class debate, public speaking, and creative programs.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/about/mission" className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:scale-105 transition-all duration-300">
                Our Mission
              </Link>
              <Link to="/about/vision" className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-green-400 hover:bg-green-50 transition-all duration-300">
                Our Vision
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section ref={ref} className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">Who We Are</h2>
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                We provide platforms for African children to develop essential life skills, showcase their talents, and connect globally. Our comprehensive approach combines debate, public speaking, arts, and sports to create well-rounded leaders of tomorrow.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaUsers className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">100+ Schools</h3>
                  <p className="text-gray-600 text-sm">Impacted across East Africa</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaGlobe className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">5+ Countries</h3>
                  <p className="text-gray-600 text-sm">Reached across Africa</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaAward className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">5000+ Students</h3>
                  <p className="text-gray-600 text-sm">Empowered and trained</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">Our Core Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do and shape the leaders of tomorrow
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreValues.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <div className={`w-12 h-12 ${value.color} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Partners & Affiliates Section - Simplified */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">Our Partners & Affiliates</h2>
            <p className="text-lg text-gray-600 mb-12 max-w-3xl mx-auto">
              Collaborating with leading organizations across Africa and beyond to deliver world-class education and opportunities for African children
            </p>

            {/* Partners Grid - Simplified */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 mb-12">
              {affiliates.map((affiliate, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex flex-col items-center group"
                >
                  <div className="relative mb-3">
                    <img 
                      src={affiliate.image}
                      alt={affiliate.name}
                      className={`
                        w-20 h-16 sm:w-24 sm:h-20 md:w-28 md:h-22
                        rounded-lg 
                        object-contain 
                        border border-gray-200 
                        shadow-md 
                        bg-white 
                        p-2 sm:p-3
                        transition-all duration-300 
                        group-hover:scale-105 
                        group-hover:shadow-lg 
                        ${affiliate.hoverColor}
                      `}
                    />
                  </div>
                  
                  <h3 className="text-xs sm:text-sm font-medium text-gray-900 text-center leading-tight">
                    {affiliate.name}
                  </h3>
                </motion.div>
              ))}
            </div>

            {/* Partnership Description - Compact Layout */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="bg-gradient-to-r from-green-50 via-blue-50 to-purple-50 rounded-2xl p-6 sm:p-8 border border-gray-200"
            >
              <div className="max-w-4xl mx-auto">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                  Building a Network of Excellence
                </h3>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6">
                  Our strategic partnerships span education, technology, and finance sectors, creating a comprehensive ecosystem that supports African children's growth and development.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-green-500 mb-2"></div>
                    <h4 className="font-semibold text-gray-900 text-sm">Educational Excellence</h4>
                    <p className="text-gray-600 text-xs">World-class curriculum</p>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-blue-500 mb-2"></div>
                    <h4 className="font-semibold text-gray-900 text-sm">Global Connections</h4>
                    <p className="text-gray-600 text-xs">International opportunities</p>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-purple-500 mb-2"></div>
                    <h4 className="font-semibold text-gray-900 text-sm">Technology Integration</h4>
                    <p className="text-gray-600 text-xs">Digital learning tools</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section - Compact Layout Matching Partners Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">Ready to Join Our Community?</h2>
            <p className="text-lg text-gray-600 mb-12 max-w-3xl mx-auto">
              Be part of the movement that's shaping confident, articulate African leaders. Your journey to excellence starts here.
            </p>

            {/* CTA Buttons Grid */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link 
                to="/register" 
                className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center justify-center"
              >
                <FaStar className="mr-2 w-4 h-4" />
                Enroll Your Child Today
              </Link>
              <Link 
                to="/contact" 
                className="px-8 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-green-400 hover:bg-green-50 hover:text-green-700 transition-all duration-300 flex items-center justify-center"
              >
                <FaHandshake className="mr-2 w-4 h-4" />
                Partner With Us
              </Link>
              <Link 
                to="/about/team" 
                className="px-8 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-green-400 hover:bg-green-50 hover:text-green-700 transition-all duration-300 flex items-center justify-center"
              >
                <FaUserFriends className="mr-2 w-4 h-4" />
                Meet Our Team
              </Link>
            </div>

            {/* Impact Stats and Contact Info - Compact Layout */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="bg-gradient-to-r from-green-50 via-blue-50 to-purple-50 rounded-2xl p-6 sm:p-8 border border-gray-200"
            >
              <div className="max-w-4xl mx-auto">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                  Join 5000+ Students Across Africa
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center mb-6">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-green-500 mb-2"></div>
                    <h4 className="font-semibold text-gray-900 text-sm">100+ Schools</h4>
                    <p className="text-gray-600 text-xs">Across East Africa</p>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-blue-500 mb-2"></div>
                    <h4 className="font-semibold text-gray-900 text-sm">5+ Countries</h4>
                    <p className="text-gray-600 text-xs">And growing</p>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-purple-500 mb-2"></div>
                    <h4 className="font-semibold text-gray-900 text-sm">Global Impact</h4>
                    <p className="text-gray-600 text-xs">Worldwide recognition</p>
                  </div>
                </div>

                <div className="text-center pt-4 border-t border-gray-200">
                  <p className="text-gray-600 text-sm mb-2">
                    <span className="font-medium">Head Office:</span> Jewel Complex next to TRM Mall on Thika Rd, 4th Floor Office No. 413.
                  </p>
                  <p className="text-gray-600 text-sm">
                    <span className="font-medium">Contact:</span> info@africandebatechampions.com • +254 718 290 692
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
